import {createVNode, render, isVNode} from 'vue';
import ToastConstructor from './Toast.vue';

const headerElement = '.header-wrap';

let seed = 1;
const GAP_SIZE = 16;

const toasts = {
  'top-left': [],
  'top-right': [],
  'bottom-left': [],
  'bottom-right': [],
};

const Toast = function (options, context) {
  options = options || {};
  const userOnClose = options.onClose;
  const id = 'toast_' + seed++;
  const position = options.position || 'top-right';
  const header = document.querySelector(headerElement);
  const headerHeight = header?.offsetHeight ?? 0;

  let verticalOffset = (options.verticalOffset || 0) + headerHeight;

  toasts[position].forEach(({vm}) => {
    verticalOffset += (vm.el?.offsetHeight || 0) + GAP_SIZE;
  });
  verticalOffset += GAP_SIZE;

  const props = {
    ...options,
    verticalOffset: verticalOffset,
    id,
    onClose: () => {
      close(id, position, userOnClose);
    },
  };

  const isElement = (e) => {
    if (typeof Element === 'undefined') return false;
    return e instanceof Element;
  };

  let appendTo = document.body;

  if (isElement(options.appendTo)) {
    appendTo = options.appendTo;
  } else if (typeof options.appendTo === 'string') {
    appendTo = document.querySelector(options.appendTo);
  }

  const container = document.createElement('div');

  const vm = createVNode(
    ToastConstructor,
    props,
    isVNode(props.message)
      ? {
          default: () => props.message,
        }
      : null
  );
  vm.appContext = context ?? Toast._context;

  vm.props.onDestroy = () => {
    render(null, container);
  };

  render(vm, container);

  toasts[position].push({vm});
  appendTo.appendChild(container.firstElementChild);

  return {
    // instead of calling the onClose function directly, setting this value so that we can have the full lifecycle
    // for out component, so that all closing steps will not be skipped.
    close: () => {
      (vm.component.exposed).visible.value = false;
    },
  };
};

['success', 'warning', 'info', 'error'].forEach((type) => {
  Toast[type] = (options) => {
    if (typeof options === 'string') {
      options = {
        message: options,
      };
    }
    options.type = type;

    return Toast(options);
  };
});

export function close(id, position, userOnClose) {
  // maybe we can store the index when inserting the vm to notification list.
  const orientedNotifications = toasts[position];
  const idx = orientedNotifications.findIndex(({vm}) => vm.component?.props.id === id);

  if (idx === -1) return;
  const {vm} = orientedNotifications[idx];
  if (!vm) return;
  // calling user's on close function before notification gets removed from DOM.
  userOnClose?.(vm);

  // note that this is called @before-leave, that's why we were able to fetch this property.
  const removedHeight = vm.el.offsetHeight;
  const verticalPos = position.split('-')[0];
  orientedNotifications.splice(idx, 1);
  const len = orientedNotifications.length;

  if (len < 1) return;
  // starting from the removing item.
  for (let i = idx; i < len; i++) {
    // new position equals the current offsetTop minus removed height plus 16px(the gap size between each item)
    const {el, component} = orientedNotifications[i].vm;
    const pos = Number.parseInt(el.style[verticalPos], 10) - removedHeight - GAP_SIZE;
    component.props.verticalOffset = pos;
  }
}

export function closeAll() {
  // loop through all directions, close them at once.
  for (const orientedNotifications of Object.values(toasts)) {
    orientedNotifications.forEach(({vm}) => {
      // same as the previous close method, we'd like to make sure lifecycle gets handle properly.
      (vm.component.exposed).visible.value = false;
    });
  }
}

Toast.closeAll = closeAll;
Toast._context = null;

export {Toast};
